/**
 * FolderBrowser — barra lateral de carpetas reutilizable para Fotos,
 * Documentos y Vídeos.
 *
 * Patrón "render prop": el padre decide cómo se renderizan los items.
 * Este componente se encarga de:
 *   - Cargar el árbol de carpetas vía `/expedientes/{id}/folders`.
 *   - Filtrar los items pasados por prop según la carpeta seleccionada.
 *   - Botones de crear / renombrar / eliminar carpeta.
 *   - Marcar carpetas como drop zones; el padre conecta el drop al
 *     PATCH del attachment.
 *
 * Uso:
 *   <FolderBrowser
 *     expedienteId={id}
 *     kind="photo"
 *     items={items}                    // attachments con .folder
 *     onMoveItem={(id, folder) => …}   // PATCH attachment
 *     renderGrid={(filtered) => <Grid>{...}</Grid>}
 *   />
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FolderSimple, FolderSimplePlus, House,
} from "@phosphor-icons/react";
import FolderRow, { NewFolderInput } from "./FolderNode";

// Carpetas "sistema": no se permite renombrar/borrar
const SYSTEM_FOLDERS = new Set(["informe", "eksvidi", "videos", "fotos"]);
const isSystem = (path) => SYSTEM_FOLDERS.has((path || "").toLowerCase());

/** Construye estructura de árbol a partir de lista plana de paths. */
function buildTree(paths) {
  const root = { name: "", path: "", children: new Map() };
  for (const p of paths) {
    let node = root;
    const parts = p.split("/").filter(Boolean);
    let acc = "";
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part;
      if (!node.children.has(part)) {
        node.children.set(part, { name: part, path: acc, children: new Map() });
      }
      node = node.children.get(part);
    }
  }
  return root;
}

/** Cuenta cuántos items hay con folder = path o cualquier subcarpeta. */
function countAt(items, path) {
  if (!path) return items.length;
  const prefix = `${path}/`;
  return items.filter((it) => {
    const f = it.folder || "";
    return f === path || f.startsWith(prefix);
  }).length;
}

export default function FolderBrowser({
  expedienteId,
  kind,
  items,
  onMoveItem,
  renderGrid,
}) {
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState(""); // "" = raíz
  const [expanded, setExpanded] = useState(() => new Set());
  const [creatingParent, setCreatingParent] = useState(null); // null o path (incl. "")
  const [creatingName, setCreatingName] = useState("");

  const loadFolders = useCallback(async () => {
    try {
      const r = await api.get(`/expedientes/${expedienteId}/folders?kind=${kind}`);
      setFolders(r.data?.folders || []);
    } catch (e) {
      console.warn("[FolderBrowser] load:", e?.message);
    }
  }, [expedienteId, kind]);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  // Re-sincroniza el árbol incluyendo paths derivados de items (por si
  // el usuario subió un archivo a una carpeta sin crearla explícitamente).
  const allPaths = useMemo(() => {
    const set = new Set(folders);
    for (const it of items) {
      const f = it.folder;
      if (!f) continue;
      const parts = f.split("/").filter(Boolean);
      for (let i = 1; i <= parts.length; i++) set.add(parts.slice(0, i).join("/"));
    }
    return Array.from(set).sort();
  }, [folders, items]);

  const tree = useMemo(() => buildTree(allPaths), [allPaths]);

  // Items filtrados según la carpeta seleccionada.
  // En la raíz (""): mostramos solo items sin folder (o con folder no en
  // árbol). En una carpeta concreta: items con folder exacto a esa.
  const filteredItems = useMemo(() => {
    if (!selected) {
      // Raíz: items SIN folder (los sueltos de toda la vida)
      return items.filter((it) => !it.folder);
    }
    return items.filter((it) => (it.folder || "") === selected);
  }, [items, selected]);

  // ============ Acciones ============
  const createFolder = async (parentPath, name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    const path = parentPath ? `${parentPath}/${clean}` : clean;
    try {
      await api.post(`/expedientes/${expedienteId}/folders`, { kind, path });
      toast.success(`Carpeta creada: ${path}`);
      setCreatingParent(null);
      setCreatingName("");
      // Asegura que el padre quede expandido para verla.
      if (parentPath) setExpanded((p) => new Set(p).add(parentPath));
      setSelected(path);
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la carpeta");
    }
  };

  const renameFolder = async (path) => {
    if (isSystem(path)) {
      toast.error("No puedes renombrar esta carpeta del sistema");
      return;
    }
    const current = path.split("/").pop();
    const next = window.prompt(`Nuevo nombre para "${current}":`, current);
    if (!next || next.trim() === current) return;
    const parts = path.split("/");
    parts[parts.length - 1] = next.trim();
    const dst = parts.join("/");
    try {
      await api.put(`/expedientes/${expedienteId}/folders/rename`, {
        kind, from: path, to: dst,
      });
      toast.success(`Renombrada a "${dst}"`);
      if (selected === path) setSelected(dst);
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo renombrar");
    }
  };

  const deleteFolder = async (path) => {
    if (isSystem(path)) {
      toast.error("No puedes eliminar esta carpeta del sistema");
      return;
    }
    const count = countAt(items, path);
    const confirmMsg = count > 0
      ? `La carpeta "${path}" contiene ${count} archivo(s). ¿Mover a la raíz y eliminar la carpeta?`
      : `¿Eliminar la carpeta vacía "${path}"?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const url = `/expedientes/${expedienteId}/folders?kind=${kind}` +
        `&path=${encodeURIComponent(path)}&cascade=${count > 0 ? "true" : "false"}`;
      await api.delete(url);
      toast.success(count > 0
        ? `Eliminada · ${count} archivo(s) movidos a la raíz`
        : "Carpeta eliminada");
      if (selected === path || selected.startsWith(`${path}/`)) setSelected("");
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const handleDropOnFolder = async (e, targetPath) => {
    e.preventDefault();
    e.stopPropagation();
    const itemId = e.dataTransfer.getData("application/x-att-id");
    if (!itemId) return;
    const it = items.find((x) => x.id === itemId);
    if (!it) return;
    if ((it.folder || "") === targetPath) return;
    try {
      await onMoveItem(itemId, targetPath || null);
      toast.success(targetPath ? `Movido a "${targetPath}"` : "Movido a la raíz");
      loadFolders();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "No se pudo mover");
    }
  };

  // ============ Render ============
  return (
    <div className="flex gap-4 h-full" data-testid={`folder-browser-${kind}`}>
      {/* Sidebar de carpetas */}
      <aside className="w-56 flex-shrink-0 border-r border-border pr-3 overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-muted">
            Carpetas
          </span>
          <button
            type="button"
            onClick={() => { setCreatingParent(""); setCreatingName(""); }}
            data-testid="folder-new-root"
            title="Nueva carpeta en la raíz"
            className="text-[11px] text-brand hover:underline inline-flex items-center gap-1"
          >
            <FolderSimplePlus className="w-3.5 h-3.5" weight="bold" /> Nueva
          </button>
        </div>

        {/* Raíz */}
        <RootRow
          selected={selected === ""}
          count={items.filter((it) => !it.folder).length}
          onClick={() => setSelected("")}
          onDrop={(e) => handleDropOnFolder(e, "")}
        />

        {/* Form de nueva carpeta raíz */}
        {creatingParent === "" && (
          <NewFolderInput
            value={creatingName}
            onChange={setCreatingName}
            onConfirm={() => createFolder("", creatingName)}
            onCancel={() => { setCreatingParent(null); setCreatingName(""); }}
          />
        )}

        {/* Árbol (renderizado como lista plana con DFS iterativo) */}
        <div className="space-y-px mt-1" data-testid={`folder-tree-${kind}`}>
          {(() => {
            // DFS iterativo: emite (node, level) y respeta expansión.
            const rows = [];
            const stack = Array.from(tree.children.values())
              .reverse()
              .map((n) => [n, 0]);
            while (stack.length) {
              const [node, level] = stack.pop();
              rows.push({ node, level });
              if (expanded.has(node.path) && node.children.size > 0) {
                const kids = Array.from(node.children.values()).reverse();
                for (const k of kids) stack.push([k, level + 1]);
              }
            }
            return rows.map(({ node, level }) => (
              <div key={node.path}>
                <FolderRow
                  node={node}
                  level={level}
                  items={items}
                  selected={selected}
                  expanded={expanded}
                  isOpen={expanded.has(node.path)}
                  hasChildren={node.children.size > 0}
                  onToggle={(p) => setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(p)) next.delete(p); else next.add(p);
                    return next;
                  })}
                  onSelect={setSelected}
                  onRename={renameFolder}
                  onDelete={deleteFolder}
                  onCreateChild={(p) => { setCreatingParent(p); setCreatingName(""); }}
                  onDrop={handleDropOnFolder}
                />
                {creatingParent === node.path && (
                  <NewFolderInput
                    value={creatingName}
                    onChange={setCreatingName}
                    onConfirm={() => createFolder(creatingParent, creatingName)}
                    onCancel={() => { setCreatingParent(null); setCreatingName(""); }}
                    level={level + 1}
                  />
                )}
              </div>
            ));
          })()}
        </div>
      </aside>

      {/* Contenido (grid) */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="text-[11px] text-muted mb-2 inline-flex items-center gap-1.5">
          {selected ? (
            <>
              <FolderSimple className="w-3.5 h-3.5" weight="duotone" />
              <span className="font-semibold text-foreground">{selected}</span>
              <span>· {filteredItems.length} en esta carpeta</span>
            </>
          ) : (
            <>
              <House className="w-3.5 h-3.5" weight="duotone" />
              <span>{filteredItems.length} archivo(s) en la raíz</span>
            </>
          )}
        </div>
        {renderGrid(filteredItems, selected)}
      </div>
    </div>
  );
}

function RootRow({ selected, count, onClick, onDrop }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { setDragOver(false); onDrop(e); }}
      data-testid="folder-row-root"
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] transition-colors ${
        selected ? "bg-brand/10 text-brand font-semibold" :
        dragOver ? "bg-amber-100 border border-amber-300" :
        "text-foreground hover:bg-background"
      }`}
    >
      <House className="w-3.5 h-3.5" weight={selected ? "fill" : "regular"} />
      <span className="flex-1 text-left truncate">Raíz</span>
      <span className="text-[10px] text-muted tabular-nums">{count}</span>
    </button>
  );
}
